
import React, { useState } from 'react';
import { 
  ShoppingBag, Video, Zap, Users, ShieldCheck, 
  Target, Megaphone, Search, Star, ArrowRight, 
  CheckCircle2, CreditCard, Sparkles, Filter
} from 'lucide-react';

interface Product {
  id: string;
  name: string;
  category: 'Branding' | 'Marketing' | 'Screening' | 'Support' | 'Software';
  price: string;
  description: string;
  features: string[];
  icon: React.ReactNode;
  popular?: boolean;
}

const PRODUCTS: Product[] = [
  {
    id: 'p1',
    name: 'Employer Branding Video',
    category: 'Branding',
    price: '€1.450',
    description: 'Een professionele cinematic video van 60 seconden.',
    features: ['1 draaidag op locatie', 'Interview met 2 medewerkers', 'Social media cuts'],
    icon: <Video className="text-white" />,
    popular: true
  },
  {
    id: 'p2',
    name: 'LinkedIn Boost Pack',
    category: 'Marketing',
    price: '€499',
    description: 'Vergroot het bereik van je vacature direct onder de juiste doelgroep.',
    features: ['Custom Ad Design', 'Targeting op concurrenten', 'Wekelijkse rapportage'],
    icon: <Megaphone className="text-white" />
  },
  {
    id: 'p3',
    name: 'AI Assessment Credits',
    category: 'Screening',
    price: '€250',
    description: '50 credits voor automatische technische testen.',
    features: ['Directe uitslag', 'Vergelijking met benchmark', 'Fraude detectie'],
    icon: <Zap className="text-slate-900" />
  },
  {
    id: 'p4',
    name: 'Psychologisch Onderzoek',
    category: 'Screening',
    price: '€850',
    description: 'Diepgaande DISC of Big Five rapportage voor je finalisten.',
    features: ['Gecertificeerde coach', '1-op-1 nabespreking', 'Culture-fit analyse'],
    icon: <ShieldCheck className="text-white" />
  },
  {
    id: 'p5',
    name: 'Sourcing Sprint (1 week)',
    category: 'Support',
    price: '€1.200',
    description: 'Intensieve headhunting door een specialist.',
    features: ['100% focus op 1 vacature', 'Minimaal 5 introducties', 'Dagelijkse standups'],
    icon: <Search className="text-white" />,
    popular: true
  },
  {
    id: 'p6',
    name: 'Referentie Check Service',
    category: 'Support',
    price: '€150',
    description: 'Wij bellen de laatste 3 werkgevers voor je na.',
    features: ['Anonieme rapportage', 'Focus op prestaties', 'Snelle verwerking'],
    icon: <Users className="text-white" />
  },
  {
    id: 'p7',
    name: 'Werftools OS (Licentie)',
    category: 'Software',
    price: 'Op aanvraag',
    description: 'Volledige toegang tot het gehele recruitmentsysteem om zelf de regie te pakken.',
    features: ['Zelf vacatures beheren', 'Froukje AI Assistent', 'Geautomatiseerde Workflows', 'Dashboarding & Analytics'],
    icon: <Sparkles className="text-white" />,
    popular: true
  }
];

const ProductCatalog: React.FC = () => {
  const [filter, setFilter] = useState<string>('Alle');

  const filteredProducts = filter === 'Alle' 
    ? PRODUCTS 
    : PRODUCTS.filter(p => p.category === filter);

  return (
    <div className="p-4 lg:p-6 space-y-4 animate-fadeIn max-w-[1600px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-black text-slate-900 tracking-tight leading-tight flex items-center gap-2">
            <ShoppingBag className="text-[#e51b23] shrink-0" size={24} />
            Marketplace
          </h1>
          <p className="text-xs text-slate-500 font-medium mt-1">Breid uw recruitment toolkit uit met premium diensten en software.</p>
        </div>
        <div className="flex bg-white p-1.5 rounded-xl border border-gray-100 shadow-sm overflow-x-auto">
          {['Alle', 'Software', 'Branding', 'Marketing', 'Screening', 'Support'].map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-5 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${
                filter === cat ? 'bg-slate-900 text-white shadow-md' : 'text-slate-400 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredProducts.map(product => (
          <div 
            key={product.id} 
            className="group bg-white rounded-[2rem] border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col overflow-hidden relative"
          >
            {product.popular && (
              <div className="absolute top-5 right-5 bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg shadow-orange-500/20">
                Populair
              </div>
            )}
            
            <div className="p-8 flex-1">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 shadow-lg ${
                  product.category === 'Branding' ? 'bg-purple-500' :
                  product.category === 'Marketing' ? 'bg-red-500' :
                  product.category === 'Screening' ? 'bg-emerald-500' :
                  product.category === 'Software' ? 'bg-indigo-600' :
                  product.category === 'Support' ? 'bg-slate-900' : 'bg-slate-900'
              }`}>
                {React.cloneElement(product.icon as React.ReactElement<any>, { size: 24 })}
              </div>
              
              <div className="mb-4">
                <span className={`text-[9px] font-black uppercase tracking-widest ${
                    product.category === 'Branding' ? 'text-purple-500' :
                    product.category === 'Marketing' ? 'text-red-500' :
                    product.category === 'Screening' ? 'text-emerald-500' :
                    product.category === 'Software' ? 'text-indigo-600' :
                    'text-slate-900'
                }`}>{product.category}</span>
                <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight mt-1">{product.name}</h3>
              </div>
              
              <p className="text-sm text-slate-500 leading-relaxed mb-6 font-medium">
                {product.description}
              </p>

              <div className="space-y-3 mb-6 bg-slate-50 p-4 rounded-xl border border-slate-100">
                {product.features.map((feature, i) => (
                  <div key={i} className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-tight text-slate-600">
                    <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-6 bg-slate-50/50 border-t border-gray-100 flex items-center justify-between">
              <div>
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">Prijs</p>
                <p className="text-xl font-black text-slate-900 mt-1">{product.price}</p>
              </div>
              <button 
                onClick={() => alert('Jouw verzoek is verstuurd! Een van onze specialisten neemt z.s.m. contact op.')}
                className="flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-black transition-all shadow-lg active:scale-95 group-hover:bg-[#e51b23]"
              >
                Vraag Aan
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductCatalog;
